import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Cell, SortOrder } from '@els/els-react--table';
import { Flyout } from '@els/els-react--flyout';
import { ButtonWithIcon } from '@els/els-react--button';
import { SortDirection } from '@Pages/RetroSynthesis/constants';
import { getSortDirectionText } from '@Pages/RetroSynthesis/components/RsTable/RsTableSortControlUtils';

import './ExamplesTableHeader.scss';

const SCORE_SORT_PARAM = 'score';
const INFO_TOOLTIP_CLASS = 'examples-table-header__info';

const SCORE_TOOLTIP =
  'Similarity score of published reaction that informed the predicted reaction based on their extended reaction centre (the atoms and bonds directly involved in the reaction and their immediate neighbourhood)';

function InfoTooltip({ id, dataE2e, label, text, width }) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;

    // The conditions table can render inside PlacementModal; ESC must close the flyout, not the modal
    const containEscape = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
      }
    };

    document.addEventListener('keydown', containEscape, true);
    return () => document.removeEventListener('keydown', containEscape, true);
  }, [isOpen]);

  return (
    <Flyout
      inline
      id={id}
      className={INFO_TOOLTIP_CLASS}
      trigger="click"
      placement="bottom-start"
      pointerType="primary"
      width={width}
      shouldCloseOnESC
      onToggle={setIsOpen}
      flyout={<span className="examples-table-header__info-text">{text}</span>}>
      <ButtonWithIcon
        data-e2e={dataE2e}
        className="u-els-margin-right-1o2"
        sprite="InformationOutlineCircle"
        iconOnly
        type="link"
        linkIconSize="xs">
        {label}
      </ButtonWithIcon>
    </Flyout>
  );
}

InfoTooltip.propTypes = {
  id: PropTypes.string.isRequired,
  dataE2e: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  width: PropTypes.string,
};

function ExamplesTableHeader({ sortList, setSortList = () => {} }) {
  const [announcement, setAnnouncement] = useState('');
  const isInitialMountRef = useRef(true);

  const scoreDirection = sortList?.find((item) => item?.property === SCORE_SORT_PARAM)?.direction;
  const isSortedByScore = Boolean(scoreDirection);
  const scoreSortOrder = scoreDirection?.toLowerCase() === SortOrder.DESC ? SortOrder.DESC : SortOrder.ASC;

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return undefined;
    }

    if (!scoreDirection) {
      return undefined;
    }

    setAnnouncement(`Table sorted by Score column in ${getSortDirectionText(scoreDirection)} order.`);
    const resetTimer = setTimeout(() => setAnnouncement(''), 600);

    return () => clearTimeout(resetTimer);
  }, [scoreDirection]);

  // Leyden puts the sort handler on the whole <th>, so clicks on the tooltip must not sort
  const handleScoreSort = (event) => {
    if (event?.target?.closest(`.${INFO_TOOLTIP_CLASS}`)) {
      return;
    }

    const nextDirection = isSortedByScore && scoreSortOrder === SortOrder.ASC ? SortDirection.DESC : SortDirection.ASC;
    setSortList([{ property: SCORE_SORT_PARAM, direction: nextDirection }]);
  };

  return (
    <tr className="c-els-table__row c-els-table__row--head examples-table-header">
      <Cell isHead cellId="select" headContentLeft={<span className="sr-only">Select</span>} />
      <Cell
        isHead
        cellId={SCORE_SORT_PARAM}
        col={SCORE_SORT_PARAM}
        colIsSortable
        sortOrder={scoreSortOrder}
        displaySortingArrow={isSortedByScore}
        onClick={handleScoreSort}
        headContentLeft={
          <InfoTooltip
            id="tooltip-score"
            dataE2e="examples-table-score-tooltip-button"
            label="Score information"
            text={SCORE_TOOLTIP}
            width="17rem"
          />
        }
        headContentRight={
          <span className="sr-only" aria-live="polite" aria-atomic="true">
            {announcement}
          </span>
        }>
        Score
      </Cell>
      <Cell
        isHead
        cellId="reaxys-examples"
        headContentLeft={
          <InfoTooltip
            id="tooltip-reaxys-examples"
            dataE2e="examples-table-published-reactions-tooltip-button"
            label="Reaxys examples information"
            text="Published reactions from patents and journals"
            width="14rem"
          />
        }>
        Reaxys examples
      </Cell>
    </tr>
  );
}

ExamplesTableHeader.propTypes = {
  sortList: PropTypes.array,
  setSortList: PropTypes.func,
};

export default ExamplesTableHeader;
